import React from 'react'
import { useTranslation } from 'react-i18next'
import { ActionColumnRenderCell, actionColumnIconStyles, actionColumnModel } from '@shared/dataGrid/columns/actionColumn'
import { can } from '@utils/permissions'
import { BaseDataGrid } from '@shared/dataGrid/BaseDataGrid'
import { GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { IconButton, Tooltip } from '@mui/material'
import { themeIcons } from '@theme/icons'
import { useNewTabHook } from '@hooks/useNewTabHook'

import { TraineesRowModel, useTraineesContext } from '../provider/TraineesProvider'

import { TraineeColumnType, traineesDataGridColumns } from './traineesDataGridColumns'

// The API only supports ordering trainees by plain scalar User fields
// (matches UserOrderField) - instructor/examDate/confirmed aren't in that
// enum (examDate maps to nextExamDate server-side, confirmed lives on
// Trainee, not User), so they stay unsortable for the server-paginated
// training-company branch. Filtering additionally special-cases
// `confirmed` on the API side (folded into the trainingCompany relation
// filter) and `profession` (remapped to professionId) - see
// GRID_FIELD_TO_API_FIELD in TraineesProvider.
const SERVER_SORTABLE_FIELDS = new Set(['email', 'lastname', 'firstname', 'trainingYear'])
const SERVER_FILTERABLE_FIELDS = new Set(['email', 'lastname', 'firstname', 'trainingYear', 'profession', 'confirmed'])

export const TraineesDataGrid: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNewTabHook()

  const {
    loading,
    traineeData,
    paginationMode,
    paginationModel,
    sortModel,
    filterModel,
    rowCount,
    updatePaginationModel,
    updateSortModel,
    updateFilterModel,
    updateInitialState,
    updateSelectedItem,
    updateSelectedIds,
    handleDisconnect,
    handleVerifyUser
  } = useTraineesContext()

  const onRowSelectionChange = React.useCallback((params: GridRowSelectionModel) => {
    updateSelectedIds(params)
  }, [])

  const handleDisconnectClick = React.useCallback((row: TraineesRowModel) => {
    updateSelectedItem(row)
    handleDisconnect(false)
  }, [])

  const columnData = React.useMemo<TraineeColumnType[]>(() => {
    return [...traineesDataGridColumns, {
      ...actionColumnModel,
      width: 150,
      renderCell: (params) => (
        <ActionColumnRenderCell
          {...(can('users:write.trainee') && !params.row.confirmed && {
            additionalActions: (
              <Tooltip title={t('trainees.verifyUserTooltip')}>
                <IconButton onClick={() => handleVerifyUser(params.row)}>
                  <themeIcons.Check sx={actionColumnIconStyles} />
                </IconButton>
              </Tooltip>
            )
          })}
          {...(can('users:read.trainee') && { onShow: () => navigate(`${params.row.id}`) })}
          // {...(can('users:write.trainee') && { onEdit: () => navigate(`${params.row.id}/edit`) })}
          {...(can('users:delete.trainee') && { onDelete: () => handleDisconnectClick(params.row) })}
        />
      )
    } as TraineeColumnType].map((column) => {
      return {
        disableColumnMenu: true,
        headerName: column.hideHeader ? '' : t(`trainees.column.${column.field}`),
        ...column,
        // Only the server-paginated training-company branch needs this
        // restriction - the (small) client-side instructor trainee list
        // keeps sorting/filtering every column as before.
        ...(paginationMode === 'server' && {
          sortable: SERVER_SORTABLE_FIELDS.has(column.field),
          filterable: SERVER_FILTERABLE_FIELDS.has(column.field)
        })
      }
    })
  }, [paginationMode])

  return (
    <BaseDataGrid
      columns={columnData}
      rows={traineeData}
      gridKey='trainees'
      loading={loading}
      onInitialState={paginationMode === 'server' ? updateInitialState : undefined}
      paginationMode={paginationMode}
      paginationModel={paginationMode === 'server' ? paginationModel : undefined}
      onPaginationModelChange={paginationMode === 'server' ? updatePaginationModel : undefined}
      rowCount={paginationMode === 'server' ? rowCount : undefined}
      sortingMode={paginationMode === 'server' ? 'server' : undefined}
      sortModel={paginationMode === 'server' ? sortModel : undefined}
      onSortModelChange={paginationMode === 'server' ? updateSortModel : undefined}
      filterMode={paginationMode === 'server' ? 'server' : undefined}
      filterModel={paginationMode === 'server' ? filterModel : undefined}
      onFilterModelChange={paginationMode === 'server' ? updateFilterModel : undefined}
      onRowSelectionModelChange={onRowSelectionChange}
    />
  )
}
